// @refresh reset
// ─────────────────────────────────────────────────────────────────────────────
// THE TAILORMIND PLATFORM — the template we clone per client.
//
// Erez, 2026-07-26: "what we're building now is the Tailormind platform... a
// template that we start with and then we can do it for each agent."
//
// This diagram exists because the architecture was being re-derived in
// conversation instead of read off a wall. It is drawn from the CODE that
// exists today (agents/otto/falcon + agents/atlas/tailormind), not from a
// plan — every box below is a real file or a real table, except the two
// marked TO BUILD.
//
// The organising claim, which is Atlas's standard on /board and holds here:
//   ENGINE and DATA are the same for every client. Only the FACE and the
//   DOMAIN change. So a new client is a clone plus four swaps, not a build.
//
// Owner: Otto. Falcon-board only — do not import into another agent's board.
// ─────────────────────────────────────────────────────────────────────────────

const C = {
  ink: "#1B211E", ink2: "#4A534D", faint: "#8C948E",
  card: "#FFFFFF", hair: "#E3DED4", paper: "#FAF7F2", bone: "#F2EDE4",
  green: "#3F8F6F", plum: "#7A5B78", amber: "#B07A1E",
  red: "#C0492F", blue: "#2F6BB0", petrol: "#2C7E9E",
};
const FONT = "'Inter Tight', Inter, system-ui, -apple-system, sans-serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

type Piece = {
  name: string;
  sub?: string;
  file?: string;          // the real file or table, so the diagram stays honest
  state?: "live" | "dormant" | "build";
};

const STATE_STYLE: Record<string, { dot: string; label: string }> = {
  live:    { dot: C.green, label: "running now" },
  dormant: { dot: C.amber, label: "in the repo, switched off" },
  build:   { dot: C.red,   label: "to build" },
};

function Dot({ state }: { state?: string }) {
  if (!state) return null;
  return <span style={{ display: "inline-block", width: 7, height: 7, borderRadius: 999,
                        background: STATE_STYLE[state].dot, flexShrink: 0 }} />;
}

function Box({ p, width }: { p: Piece; width?: number }) {
  return (
    <div style={{
      width, boxSizing: "border-box", background: C.card, border: `1px solid ${C.hair}`,
      borderRadius: 10, padding: "10px 12px", fontFamily: FONT,
      display: "flex", flexDirection: "column", gap: 3, minWidth: 0,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
        <Dot state={p.state} />
        <span style={{ fontSize: 13.5, fontWeight: 600, color: C.ink, lineHeight: 1.25 }}>{p.name}</span>
      </div>
      {p.sub && <div style={{ fontSize: 12, lineHeight: 1.4, color: C.ink2 }}>{p.sub}</div>}
      {p.file && <div style={{ fontFamily: MONO, fontSize: 10.5, color: C.faint }}>{p.file}</div>}
    </div>
  );
}

function Band({ tag, tone, title, note, children }: {
  tag: string; tone: string; title: string; note?: string; children: React.ReactNode;
}) {
  return (
    <div style={{ display: "flex", gap: 14, alignItems: "stretch" }}>
      {/* the layer's spine — colour-coded so the three layers read at a glance */}
      <div style={{ width: 4, borderRadius: 999, background: tone, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 8 }}>
          <span style={{ fontFamily: MONO, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.14em",
                         textTransform: "uppercase", color: tone }}>{tag}</span>
          <span style={{ fontSize: 15, fontWeight: 600, color: C.ink }}>{title}</span>
          {note && <span style={{ fontSize: 12.5, color: C.faint }}>{note}</span>}
        </div>
        {children}
      </div>
    </div>
  );
}

const Row = ({ children, cols }: { children: React.ReactNode; cols: string }) => (
  <div style={{ display: "grid", gridTemplateColumns: cols, gap: 10 }}>{children}</div>
);

// An arrow between bands. Deliberately literal about DIRECTION — the whole
// point of the diagram is that everything funnels through one place.
function Flow({ label }: { label: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "9px 0 9px 18px" }}>
      <span style={{ color: C.faint, fontSize: 13 }}>↓</span>
      <span style={{ fontFamily: MONO, fontSize: 10.5, letterSpacing: "0.08em",
                     textTransform: "uppercase", color: C.faint }}>{label}</span>
    </div>
  );
}

export function PlatformArchitecture() {
  return (
    <div data-zoom style={{
      width: 1400, boxSizing: "border-box", background: C.card, border: `1px solid ${C.hair}`,
      borderRadius: 18, boxShadow: "0 10px 30px rgba(27,33,30,0.05)", padding: 30,
      fontFamily: FONT, display: "flex", flexDirection: "column", gap: 4,
    }}>
      {/* ── header ─────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 20 }}>
        <div>
          <div style={{ fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: "0.14em",
                        textTransform: "uppercase", color: C.petrol }}>
            THE TAILORMIND PLATFORM · ONE BOX PER CLIENT
          </div>
          <div style={{ fontSize: 25, fontWeight: 600, color: C.ink, letterSpacing: "-0.015em", marginTop: 4 }}>
            Engine and data are the same for everyone.<br />Only the face and the domain change.
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 5, paddingTop: 4 }}>
          {(["live", "dormant", "build"] as const).map((k) => (
            <div key={k} style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <Dot state={k} />
              <span style={{ fontSize: 11.5, color: C.ink2, whiteSpace: "nowrap" }}>{STATE_STYLE[k].label}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ height: 1, background: C.hair, margin: "18px 0 20px" }} />

      {/* ── 1. DOORS ───────────────────────────────────────────────────── */}
      <Band tag="Doors" tone={C.plum} title="How the world reaches the box"
            note="deliberately unequal — each can do something the others cannot">
        <Row cols="repeat(3, 1fr)">
          <Box p={{ name: "Telegram", sub: "The daily surface. Voice + media. The ONLY door that can START a conversation (push).", file: "telegram/runner.py · webhook.py", state: "live" }} />
          <Box p={{ name: "Console (web app)", sub: "The ONLY door that can CORRECT. Review queue, tables, boards, analytics. For the owner and his staff.", file: "frontend/ — to be replaced", state: "build" }} />
          <Box p={{ name: "MCP", sub: "External door for FOREIGN agents (ChatGPT). Read-only by design. Never the internal plumbing.", file: "—", state: "build" }} />
        </Row>
      </Band>

      <Flow label="every door calls the same API — no door talks to Postgres" />

      {/* ── 2. SERVICE ─────────────────────────────────────────────────── */}
      <Band tag="Service" tone={C.blue} title="One API — the only thing that touches the database"
            note="where every invariant lives, exactly once">
        <Row cols="repeat(5, 1fr)">
          <Box p={{ name: "Auth + roles", sub: "business / admin. Two consoles, one app.", file: "auth.py · app_users", state: "dormant" }} />
          <Box p={{ name: "WRITE GATE", sub: "Tools propose; only this persists. Provenance + confidence PER FIELD.", file: "capture/gate.py", state: "live" }} />
          <Box p={{ name: "GUARDRAIL GATE", sub: "The single door OUT. Nothing reaches a human unapproved.", file: "guardrail.py", state: "dormant" }} />
          <Box p={{ name: "Outbox", sub: "Idempotent send. A retry never double-sends.", file: "outbox.py · sender.py", state: "dormant" }} />
          <Box p={{ name: "Spend + budget", sub: "Per-tenant cap, fails CLOSED.", file: "spend.py · gateway.py", state: "live" }} />
        </Row>
      </Band>

      <Flow label="two ways to execute work — one brain underneath" />

      {/* ── 3. EXECUTION ───────────────────────────────────────────────── */}
      <Band tag="Execution" tone={C.green} title="Two modes, and the rule for choosing"
            note="the piece that was missing from the conversation">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          {/* agent side */}
          <div style={{ background: C.paper, border: `1px solid ${C.hair}`, borderRadius: 12, padding: 14 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: C.ink, marginBottom: 3 }}>AGENT LOOP</div>
            <div style={{ fontSize: 12, color: C.ink2, marginBottom: 10, lineHeight: 1.45 }}>
              When a <b>human starts it</b>, it takes several turns, it must be able to <b>ask a question mid-way</b>, and it <b>writes</b> data.
              <i> Falcon flows 1–5, 7–10.</i>
            </div>
            <Row cols="1fr 1fr">
              <Box p={{ name: "Pydantic-AI runtime", sub: "model + tools + typed history, capped at 8 hops", file: "agent/runtime.py", state: "live" }} />
              <Box p={{ name: "Tools", sub: "the per-client swap point", file: "agent/tools_impl.py", state: "live" }} />
            </Row>
          </div>
          {/* workflow side */}
          <div style={{ background: C.paper, border: `1px solid ${C.hair}`, borderRadius: 12, padding: 14 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: C.ink, marginBottom: 3 }}>WORKFLOW ENGINE</div>
            <div style={{ fontSize: 12, color: C.ink2, marginBottom: 10, lineHeight: 1.45 }}>
              When <b>nobody starts it</b> — it wakes on a clock, the shape is known, and latency must be bounded.
              <i> Falcon flows 6, 11, 12 + staleness.</i>
            </div>
            <Row cols="1fr 1fr">
              <Box p={{ name: "JSON step-list", sub: "version-frozen; DBOS durable", file: "workflows/engine.py", state: "dormant" }} />
              <Box p={{ name: "Step registry", sub: "one decorator = one new capability", file: "workflows/steps.py", state: "dormant" }} />
            </Row>
          </div>
        </div>

        {/* the extension point Erez asked for */}
        <div style={{ marginTop: 12, background: "#FBF6EE", border: `1px dashed ${C.amber}`,
                      borderRadius: 12, padding: "13px 16px" }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: C.ink }}>
            ⚡ <code style={{ fontFamily: MONO, fontSize: 13 }}>@step("run_agent")</code> — the bridge, and the answer to "can we trigger Claude Code?"
          </div>
          <div style={{ fontSize: 12.5, color: C.ink2, lineHeight: 1.5, marginTop: 5 }}>
            A step is registered with a <b>one-line decorator</b>, so handing a job to <b>Claude Code, Pi, or Hermes</b> is a
            new <i>step type</i> — not a new architecture. A scheduled, deterministic workflow can invoke a
            non-deterministic agent for one step and keep the result inside the same transaction, the same audit trail
            and the same budget. <b>This is the single file that makes the platform open-ended.</b>
          </div>
        </div>
      </Band>

      <Flow label="capture only for clients who AUTHOR data rather than copy it" />

      {/* ── 4. CAPTURE ─────────────────────────────────────────────────── */}
      <Band tag="Capture" tone={C.amber} title="Turning speech into facts"
            note="optional module — Falcon needs it, Boiling Offers does not">
        <Row cols="repeat(4, 1fr)">
          <Box p={{ name: "Speech in", sub: "ivrit-ai large-v3-turbo, ~1.2× realtime", file: "capture/listen.py", state: "live" }} />
          <Box p={{ name: "Extraction", sub: "transcript → proposed facts + confidence from evidence", file: "capture/extract.py", state: "live" }} />
          <Box p={{ name: "Speech out", sub: "Kokoro + Phonikud, pure am_echo", file: "capture/speech.py", state: "live" }} />
          <Box p={{ name: "Media archive", sub: "the original audio is kept — the transcript is derived", file: "media table", state: "live" }} />
        </Row>
      </Band>

      <Flow label="one write path in — RLS underneath" />

      {/* ── 5. DATA ────────────────────────────────────────────────────── */}
      <Band tag="Data" tone={C.petrol} title="Postgres, row-level security, one box"
            note="platform tables are identical for every client">
        <Row cols="repeat(3, 1fr)">
          <Box p={{ name: "Platform tables — SAME for everyone", sub: "tenants · app_users · chats · messages · outbox · workflow_defs · workflow_runs · approvals · audit_log · spend_lines · budgets · brain_facts", file: "sql/001–011", state: "live" }} />
          <Box p={{ name: "Domain tables — the client's business", sub: "Falcon: properties · clients · offers · media  |  Boiling Offers: deals · leads · partners", file: "sql/012_falcon_domain.sql", state: "live" }} />
          <Box p={{ name: "field_events — the spine of trust", sub: "append-only. One row per field, per assertion, with source + confidence. This is what makes a disputed deal provable.", file: "field_events", state: "live" }} />
        </Row>
      </Band>

      <div style={{ height: 1, background: C.hair, margin: "22px 0 18px" }} />

      {/* ── the payoff: what a new client actually costs ───────────────── */}
      <div style={{ display: "flex", gap: 18, alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: MONO, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.14em",
                        textTransform: "uppercase", color: C.ink }}>A new client = clone + four swaps</div>
          <div style={{ fontSize: 12.5, color: C.ink2, marginTop: 6, lineHeight: 1.5 }}>
            Everything above stays. These four are the only per-client work — which is what makes it a
            <b> platform</b> rather than a series of projects.
          </div>
        </div>
        <div style={{ flex: 2 }}>
          <Row cols="repeat(4, 1fr)">
            <Box p={{ name: "1 · Domain tables", sub: "what this business actually has" }} />
            <Box p={{ name: "2 · Agent tools", sub: "what it can be asked to do" }} />
            <Box p={{ name: "3 · Workflow steps", sub: "what runs unattended" }} />
            <Box p={{ name: "4 · Console pages", sub: "what the owner opens" }} />
          </Row>
        </div>
      </div>

      {/* ── the honest gap ─────────────────────────────────────────────── */}
      <div style={{ marginTop: 16, background: C.bone, borderRadius: 12, padding: "13px 16px" }}>
        <div style={{ fontSize: 12.5, color: C.ink2, lineHeight: 1.55 }}>
          <b>Where the template is genuinely incomplete, stated plainly.</b> The <b>ENGINE and DATA layers exist and are
          proven</b> — Falcon inherited 16 spine files byte-identical from Boiling Offers and all 22 platform tables.
          The <b>FACE does not</b>: Boiling Offers' front end is ~1,700 lines of bespoke JSX with no boards, no tables and
          no knowledge base. <b>That is the piece to throw away and rebuild once, from the dashboard template's shell,
          so every future client inherits a real console.</b> Second gap: today the agent's tools open a database
          session directly — inherited from Boiling Offers, fine on one box, wrong the moment a second door exists.
          They should call the service API like everyone else.
        </div>
      </div>
    </div>
  );
}
